import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getReportsByOrg, getSites, getOrgDocuments, getMyTrainingRecords, getOrgLicenceSummary } from '../api';
import EmptyState from '../components/EmptyState';

const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' },
  IN_PROGRESS: { label: 'In Progress', color: '#f59e0b', bg: '#fffbeb' },
  REVIEW: { label: 'Review', color: '#3b82f6', bg: '#eff6ff' },
  COMPLETED: { label: 'Completed', color: '#10b981', bg: '#ecfdf5' },
};

const cardStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '1.25rem',
  flex: '1 1 200px',
  minWidth: 160,
};

const widgetStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '1.25rem',
  flex: '1 1 320px',
  minWidth: 280,
};

const clickableCardStyle = {
  ...cardStyle,
  cursor: 'pointer',
  transition: 'box-shadow 0.2s, transform 0.15s',
};

const clickableWidgetStyle = {
  ...widgetStyle,
  cursor: 'pointer',
  transition: 'box-shadow 0.2s, transform 0.15s',
};

const numberStyle = { fontSize: '2rem', fontWeight: 700, lineHeight: 1.1 };
const labelStyle = { fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' };
const sectionTitle = { fontSize: '1rem', color: '#555', marginBottom: '0.75rem' };

function ProgressRing({ percent, color, size = 80, strokeWidth = 8 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  );
}

function HorizontalBar({ label, value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 2 }}>
        <span style={{ color: '#333' }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{value}</span>
      </div>
      <div style={{ background: '#f3f4f6', borderRadius: 4, height: 8, overflow: 'hidden' }}>
        <div style={{ background: color, height: '100%', width: `${pct}%`, borderRadius: 4, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

function MonthBar({ label, value, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#333', marginBottom: 2 }}>{value || ''}</div>
      <div style={{ width: '100%', maxWidth: 28, background: '#f3f4f6', borderRadius: 3, height: 60, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', overflow: 'hidden' }}>
        <div style={{ background: '#4361ee', borderRadius: 3, height: `${pct}%`, transition: 'height 0.4s ease' }} />
      </div>
      <div style={{ fontSize: '0.6rem', color: '#888', marginTop: 2, whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, isAnyAdmin, isSystemAdmin } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [siteCount, setSiteCount] = useState(0);
  const [orgDocuments, setOrgDocuments] = useState([]);
  const [trainingRecords, setTrainingRecords] = useState([]);
  const [licenceSummaries, setLicenceSummaries] = useState([]);
  const [loading, setLoading] = useState(true);

  const orgAssignments = user?.orgAssignments || [];
  const orgIds = orgAssignments.map((a) => a.organisationId);

  useEffect(() => {
    if (orgIds.length === 0) { setLoading(false); return; }

    const reportPromises = orgIds.map((id) =>
      getReportsByOrg(id, { page: 0, size: 1000, sortBy: 'assessmentDate', sortDir: 'desc' })
        .then((r) => r.data)
        .catch(() => [])
    );
    const sitePromises = orgIds.map((id) => getSites(id).catch(() => []));
    const docPromises = orgIds.map((id) => getOrgDocuments(id).catch(() => []));
    const trainingPromise = getMyTrainingRecords().catch(() => []);
    const licencePromises = orgIds.map((id) => getOrgLicenceSummary(id).catch(() => null));

    Promise.all([
      Promise.all(reportPromises),
      Promise.all(sitePromises),
      Promise.all(docPromises),
      trainingPromise,
      Promise.all(licencePromises),
    ])
      .then(([reportResults, siteResults, docResults, training, licenceResults]) => {
        setReports(reportResults.flat().sort((a, b) => (b.assessmentDate || '').localeCompare(a.assessmentDate || '')));
        setSiteCount(siteResults.flat().length);
        setOrgDocuments(docResults.flat());
        setTrainingRecords(training);
        setLicenceSummaries(licenceResults.filter(Boolean));
      })
      .finally(() => setLoading(false));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Computed data ---
  const statusCounts = reports.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const uniqueSites = new Set(reports.map((r) => r.siteName).filter(Boolean)).size;
  const recent = reports.slice(0, 5);

  // Review due / overdue
  const now = new Date();
  const soon = new Date();
  soon.setDate(soon.getDate() + 30);
  const reviewDue = reports.filter((r) => {
    if (!r.reviewDate || r.status === 'ASSESSOR_COMPLETED') return false;
    return new Date(r.reviewDate) <= soon;
  });
  const overdue = reviewDue.filter((r) => new Date(r.reviewDate) < now);

  // Completion rate
  const completedCount = statusCounts['ASSESSOR_COMPLETED'] || 0;
  const completionPct = reports.length > 0 ? Math.round((completedCount / reports.length) * 100) : 0;

  // Reports by assessor
  const assessorCounts = reports.reduce((acc, r) => {
    const name = r.assessorName || 'Unknown';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const assessorEntries = Object.entries(assessorCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const maxAssessorCount = assessorEntries.length > 0 ? assessorEntries[0][1] : 0;

  // Monthly trend (last 12 months)
  const monthLabels = [];
  const monthCounts = {};
  for (let i = 11; i >= 0; i--) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    const label = d.toLocaleDateString('en-GB', { month: 'short' });
    monthLabels.push({ key, label });
    monthCounts[key] = 0;
  }
  reports.forEach((r) => {
    if (!r.assessmentDate) return;
    const key = r.assessmentDate.substring(0, 7);
    if (monthCounts[key] !== undefined) monthCounts[key]++;
  });
  const maxMonth = Math.max(...Object.values(monthCounts), 1);

  // Expiring certifications
  const expiringDocs = orgDocuments.filter((d) => {
    if (!d.expiryDate) return false;
    return new Date(d.expiryDate) <= soon;
  }).sort((a, b) => (a.expiryDate || '').localeCompare(b.expiryDate || ''));
  const expiredDocs = expiringDocs.filter((d) => new Date(d.expiryDate) < now);

  // Training compliance
  const validTraining = trainingRecords.filter((t) => !t.expiryDate || new Date(t.expiryDate) >= now);
  const expiredTraining = trainingRecords.filter((t) => t.expiryDate && new Date(t.expiryDate) < now);
  const expiringTraining = trainingRecords.filter((t) => {
    if (!t.expiryDate) return false;
    const ed = new Date(t.expiryDate);
    return ed >= now && ed <= soon;
  });
  const trainingCompliancePct = trainingRecords.length > 0
    ? Math.round((validTraining.length / trainingRecords.length) * 100) : 100;

  const ASSESSOR_COLORS = ['#4361ee', '#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'];

  return (
    <div className="page-container">
      <div className="form-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>Welcome, {user.firstName}!</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to="/reports/new" className="btn btn-primary" style={{ fontSize: '0.85rem' }}>New Report</Link>
            <Link to="/reports" className="btn btn-outline" style={{ fontSize: '0.85rem', color: '#333', borderColor: '#ccc' }}>All Reports</Link>
          </div>
        </div>

        {loading ? <p>Loading dashboard...</p> : (
          <>
            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div style={clickableCardStyle} onClick={() => navigate('/reports')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ ...numberStyle, color: '#1a1a2e' }}>{reports.length}</div>
                <div style={labelStyle}>Total Reports</div>
              </div>
              <div style={clickableCardStyle} onClick={() => navigate('/reports')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ ...numberStyle, color: '#6366f1' }}>{uniqueSites}</div>
                <div style={labelStyle}>Sites Assessed</div>
              </div>
              <div style={clickableCardStyle} onClick={() => navigate('/organisations')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ ...numberStyle, color: '#8b5cf6' }}>{orgAssignments.length}</div>
                <div style={labelStyle}>Organisations</div>
              </div>
              <div style={clickableCardStyle} onClick={() => navigate('/sites')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ ...numberStyle, color: '#0891b2' }}>{siteCount}</div>
                <div style={labelStyle}>Sites</div>
              </div>
            </div>

            {/* Status Breakdown */}
            <h3 style={sectionTitle}>Reports by Status</h3>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {Object.entries(STATUS_CONFIG).map(([status, cfg]) => (
                <div key={status} style={{ ...clickableCardStyle, background: cfg.bg, borderColor: cfg.color + '33', textAlign: 'center', flex: '1 1 140px', minWidth: 120 }} onClick={() => navigate(`/reports?status=${status}`)} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                  <div style={{ ...numberStyle, color: cfg.color }}>{statusCounts[status] || 0}</div>
                  <div style={{ ...labelStyle, color: cfg.color }}>{cfg.label}</div>
                </div>
              ))}
            </div>

            {/* Alerts */}
            {overdue.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#991b1b' }}>
                <strong>{overdue.length} report{overdue.length !== 1 ? 's' : ''} overdue for review</strong>
                {' \u2014 '}
                {overdue.slice(0, 3).map((r) => r.siteName || `#${r.id}`).join(', ')}
                {overdue.length > 3 && ` and ${overdue.length - 3} more`}
              </div>
            )}
            {reviewDue.length > overdue.length && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#92400e' }}>
                <strong>{reviewDue.length - overdue.length} report{reviewDue.length - overdue.length !== 1 ? 's' : ''} due for review within 30 days</strong>
              </div>
            )}

            {/* Widget Row: Completion Rate + Monthly Trend */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {/* Completion Rate */}
              <div style={clickableWidgetStyle} onClick={() => navigate('/reports?status=COMPLETED')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <h3 style={{ ...sectionTitle, marginBottom: '1rem' }}>Completion Rate</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                  <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
                    <ProgressRing percent={completionPct} color="#10b981" />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem', color: '#10b981' }}>
                      {completionPct}%
                    </div>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#555' }}>
                    <div><strong>{completedCount}</strong> of <strong>{reports.length}</strong> reports completed</div>
                    {(statusCounts['DRAFT'] || 0) > 0 && <div style={{ color: '#6b7280', marginTop: 4 }}>{statusCounts['DRAFT']} still in draft</div>}
                    {(statusCounts['IN_PROGRESS'] || 0) > 0 && <div style={{ color: '#f59e0b', marginTop: 2 }}>{statusCounts['IN_PROGRESS']} in progress</div>}
                  </div>
                </div>
              </div>

              {/* Monthly Trend */}
              <div style={clickableWidgetStyle} onClick={() => navigate('/reports')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <h3 style={{ ...sectionTitle, marginBottom: '1rem' }}>Monthly Trend (12 months)</h3>
                <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
                  {monthLabels.map((m) => (
                    <MonthBar key={m.key} label={m.label} value={monthCounts[m.key]} max={maxMonth} />
                  ))}
                </div>
              </div>
            </div>

            {/* Widget Row: Reports by Assessor + Training & Certifications */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {/* Reports by Assessor */}
              <div style={widgetStyle}>
                <h3 style={{ ...sectionTitle, marginBottom: '0.75rem' }}>Reports by Assessor</h3>
                {assessorEntries.length === 0 ? (
                  <p style={{ color: '#888', fontSize: '0.85rem' }}>No assessor data</p>
                ) : (
                  assessorEntries.map(([name, count], i) => (
                    <div key={name} style={{ cursor: 'pointer' }} onClick={() => navigate(`/reports?search=${encodeURIComponent(name)}`)}>
                      <HorizontalBar label={name} value={count} max={maxAssessorCount} color={ASSESSOR_COLORS[i] || '#aaa'} />
                    </div>
                  ))
                )}
              </div>

              {/* Training & Certifications */}
              <div style={widgetStyle}>
                <h3 style={{ ...sectionTitle, marginBottom: '1rem' }}>Training & Certifications</h3>
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => navigate('/training-records')}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ position: 'relative', width: 64, height: 64 }}>
                      <ProgressRing percent={trainingCompliancePct} color={trainingCompliancePct === 100 ? '#10b981' : '#f59e0b'} size={64} strokeWidth={6} />
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', color: trainingCompliancePct === 100 ? '#10b981' : '#f59e0b' }}>
                        {trainingCompliancePct}%
                      </div>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: 4 }}>Training</div>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#555', flex: 1 }}>
                    <div>{validTraining.length} valid record{validTraining.length !== 1 ? 's' : ''}</div>
                    {expiredTraining.length > 0 && <div style={{ color: '#dc2626', marginTop: 2 }}>{expiredTraining.length} expired</div>}
                    {expiringTraining.length > 0 && <div style={{ color: '#f59e0b', marginTop: 2 }}>{expiringTraining.length} expiring within 30 days</div>}
                    {trainingRecords.length === 0 && <div style={{ color: '#888' }}>No training records</div>}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', cursor: 'pointer' }} onClick={() => navigate('/org-documents')}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#555', marginBottom: '0.5rem' }}>Certifications</div>
                  {orgDocuments.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', color: '#888' }}>No certifications on file</div>
                  ) : (
                    <div style={{ fontSize: '0.85rem', color: '#555' }}>
                      <div>{orgDocuments.length} document{orgDocuments.length !== 1 ? 's' : ''} on file</div>
                      {expiredDocs.length > 0 && (
                        <div style={{ color: '#dc2626', marginTop: 2 }}>
                          {expiredDocs.length} expired: {expiredDocs.slice(0, 2).map((d) => d.title).join(', ')}
                          {expiredDocs.length > 2 && ` +${expiredDocs.length - 2} more`}
                        </div>
                      )}
                      {expiringDocs.length > expiredDocs.length && (
                        <div style={{ color: '#f59e0b', marginTop: 2 }}>
                          {expiringDocs.length - expiredDocs.length} expiring within 30 days
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Licence Credits */}
            {licenceSummaries.length > 0 && (
              <>
                <h3 style={sectionTitle}>Licence Credits</h3>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  {licenceSummaries.map((ls) => {
                    const pct = ls.totalCredits > 0 ? Math.round((ls.creditsRemaining / ls.totalCredits) * 100) : 0;
                    const color = ls.creditsRemaining > 5 ? '#10b981' : ls.creditsRemaining > 0 ? '#f59e0b' : '#dc2626';
                    return (
                      <div key={ls.organisationId} style={clickableWidgetStyle} onClick={() => navigate(isAnyAdmin ? '/org-licences' : '/reports')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                          <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
                            <ProgressRing percent={pct} color={color} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.1rem', color }}>
                              {ls.creditsRemaining}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#333', marginBottom: 4 }}>{ls.organisationName}</div>
                            <div style={{ fontSize: '0.85rem', color: '#555' }}>
                              <span style={{ fontWeight: 600 }}>{ls.creditsUsed}</span> used of <span style={{ fontWeight: 600 }}>{ls.totalCredits}</span> total
                            </div>
                            <div style={{ fontSize: '0.8rem', color, marginTop: 2 }}>
                              {ls.creditsRemaining} credit{ls.creditsRemaining !== 1 ? 's' : ''} remaining
                            </div>
                            {ls.creditsRemaining === 0 && (
                              <div style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 600, marginTop: 4 }}>
                                No credits — redeem a licence to create reports
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {isSystemAdmin && (
                    <div style={clickableWidgetStyle} onClick={() => navigate('/licences')} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 80 }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '1.5rem', color: '#4361ee', marginBottom: 4 }}>+</div>
                          <div style={{ fontSize: '0.9rem', color: '#4361ee', fontWeight: 600 }}>Manage Licences</div>
                          <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 2 }}>Create & view all licences</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Recent Reports */}
            <h3 style={sectionTitle}>Recent Reports</h3>
            {recent.length === 0 ? (
              <EmptyState message="No reports yet."><Link to="/reports/new">Create your first report</Link></EmptyState>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr><th>Site</th><th>Organisation</th><th>Date</th><th>Assessor</th><th>Status</th><th></th></tr>
                  </thead>
                  <tbody>
                    {recent.map((r) => (
                      <tr key={r.id}>
                        <td>{r.siteName || '\u2014'}</td>
                        <td>{r.organisationName || '\u2014'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.assessmentDate || '\u2014'}</td>
                        <td>{r.assessorName || '\u2014'}</td>
                        <td>
                          <span style={{
                            background: STATUS_CONFIG[r.status]?.color || '#6b7280',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: '0.75rem',
                          }}>
                            {STATUS_CONFIG[r.status]?.label || r.status}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Link to={`/reports/${r.id}/view`}>View</Link>
                          <Link to={`/reports/${r.id}`} style={{ marginLeft: 8 }}>Edit</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Quick Links */}
            <h3 style={{ ...sectionTitle, margin: '1.5rem 0 0.75rem' }}>Quick Links</h3>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Link to="/sites" className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc', fontSize: '0.85rem' }}>Manage Sites</Link>
              <Link to="/organisations" className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc', fontSize: '0.85rem' }}>Organisations</Link>
              <Link to="/training-records" className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc', fontSize: '0.85rem' }}>Training Records</Link>
              <Link to="/org-documents" className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc', fontSize: '0.85rem' }}>Certifications</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
